import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Typography, Container, Box, CircularProgress,
  Alert, Snackbar, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import {
  DocumentScannerOutlined, ErrorOutline, LocalShippingOutlined,
  PaymentOutlined, PrivacyTipOutlined, ReviewsOutlined,
  Storefront, SupportAgentOutlined,
} from "@mui/icons-material";
import HandymanIcon from "@mui/icons-material/Handyman";
import CampaignIcon from "@mui/icons-material/Campaign";
import PricingCard from "../../components/pricing/PricingCard";
import FAQs from "../../components/pricing/FAQs";
import config from "../../config";
import { connect } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const Membership = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [planType, setPlanType] = useState("month");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    const getPlans = async () => {
      try {
        const res = await axios.get(`${config.SERVER_URL}/api/v1/plans`);
        if (res.status === 200) {
          setPlans(res.data);
          setFilteredPlans(res.data.filter(p => p.interval === "month" || p.name === "Diamond Special"));
        }
      } catch {
        setSnackbar({ open: true, message: "Unable to load plans right now. Please try again later.", severity: "error" });
      }
      setLoading(false);
    };
    getPlans();
  }, []);

  useEffect(() => {
    if (planType === "month") {
      setFilteredPlans(plans.filter(p => p.interval === "month" || p.name === "Diamond Special"));
    } else {
      setFilteredPlans(plans.filter(p => p.interval === "year" || p.name === "Diamond Special"));
    }
  }, [planType, plans]);

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/pricing-preview.png" />
        <meta property="og:title" content="Pricing" />
        <meta property="og:description" content="Moore Exotics Pricing" />
      </Helmet>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled" severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Hero ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 5, md: 7 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, justifyContent: "center" }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Pricing</Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: "30px", md: "50px" }, color: "#fff", lineHeight: 1.1, mb: 1.5 }}>
              Simple, Transparent <Box component="span" sx={{ color: "#FFC001" }}>Pricing</Box>
            </Typography>
            <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" }, maxWidth: 500, mx: "auto", mb: 4 }}>
              Choose the plan that fits your business. Upgrade or cancel anytime.
            </Typography>

            {/* Monthly / Yearly toggle */}
            <Box sx={{ display: "inline-flex", backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "12px", p: 0.5 }}>
              <ToggleButtonGroup
                value={planType}
                exclusive
                onChange={(_, val) => { if (val) setPlanType(val); }}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "none", borderRadius: "9px !important", px: 3, py: 0.8,
                    color: "#666", fontWeight: 700, fontSize: "13px", textTransform: "none",
                    transition: "all 0.2s",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "#FFC001", color: "#000",
                    "&:hover": { backgroundColor: "#e6ac00" },
                  },
                  "& .MuiToggleButton-root:not(.Mui-selected):hover": {
                    backgroundColor: "rgba(255,255,255,0.05)",
                    color: "#ccc",
                  },
                }}
              >
                <ToggleButton value="month">Monthly</ToggleButton>
                <ToggleButton value="year">
                  Yearly
                  <Box component="span" sx={{ ml: 1, backgroundColor: "rgba(255,192,1,0.15)", color: "#FFC001", fontSize: "10px", fontWeight: 800, px: 0.8, py: 0.2, borderRadius: "4px" }}>
                    SAVE
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Container>
        </Box>

        {/* ── Plans grid ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#FFC001" }} />
            </Box>
          ) : (
            <Box sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: `repeat(${Math.min(filteredPlans.length, 3)}, 1fr)`,
              },
              alignItems: "stretch",
            }}>
              {filteredPlans.map((plan, i) => (
                <PricingCard
                  key={plan.id}
                  plan={plan}
                  user={user}
                  planType={planType}
                  isPopular={i === 1}
                  handleShowMonthly={() => setPlanType("month")}
                  handleShowYearly={() => setPlanType("year")}
                />
              ))}
            </Box>
          )}
        </Container>

        {/* ── FAQs ── */}
        <Box sx={{ backgroundColor: "#0d0d0d", borderTop: "1px solid #1a1a1a", py: { xs: 6, md: 8 } }}>
          <Container maxWidth="md">
            <FAQs faqs={faqs} />
          </Container>
        </Box>
      </Box>
    </>
  );
};

const faqs = [
  {
    icon: LocalShippingOutlined,
    title: "How do I promote my services on your site?",
    paragraph: "To promote yourself or your business on our site, simply create your account and select which membership plan works best for you.",
  },
  {
    icon: Storefront,
    title: "In regards to the purchase of animals, is it illegal to do so on your website?",
    paragraph: "We comply with all rules and regulations that are enforced by state and federal laws. If you are interested in partnering up with a breeder, we make sure that all sellers adhere to the necessary procedures when selling and distributing animals. Please view your state's current rules and regulations regarding the purchase of exotic animals.",
  },
  {
    icon: SupportAgentOutlined,
    title: "How do I contact customer service if I have any issues or concerns?",
    paragraph: "You can contact our customer service team by email, phone, or through our social media outlets. We are always happy to assist you with any questions or concerns you may have.",
  },
  {
    icon: ErrorOutline,
    title: "What happens if there is an issue with the service I selected?",
    paragraph: "As a hub for all things animals and services for your animals, we do not collect any kind of payment in regards to pet services. If there is an issue with the service you selected, we kindly urge you to contact the business themselves. However, if for any reason the business raises any kind of concern, we would be happy to look into it further with you.",
  },
  {
    icon: PaymentOutlined,
    title: "How do I get a refund in regards to the service I selected?",
    paragraph: "We understand that some things do not go the way we want them to. We do not collect any kind of payment in regards to services for your pet. Please follow up with the business that provided the service to you, in regard to your refund.",
  },
  {
    icon: DocumentScannerOutlined,
    title: "What forms of payment do you take?",
    paragraph: "We make finding and selecting services for your pets easy and convenient by offering a variety of payment options. Currently, we accept major debit and credit cards, and we're constantly working to expand our payment options to make buying pets even more seamless.",
  },
  {
    icon: ReviewsOutlined,
    title: "How do I know if a business on your site is a reputable business?",
    paragraph: "We take safety and credibility very seriously. We have a thorough evaluation process that includes verifying each business before they are allowed to sell through our marketplace. We carefully monitor each seller's activities on the site to ensure they comply with our strict standards.",
  },
  {
    icon: HandymanIcon,
    title: "Will any documentation be needed for my business when using your service?",
    paragraph: "Compliance with regulations and legal requirements is very important to us. We ensure that all necessary documentation and licensing are provided by each business, and we work closely with all businesses to make sure they meet all legal requirements in regards to their business practices.",
  },
  {
    icon: CampaignIcon,
    title: "Do you want to advertise on our site?",
    paragraph: "If you want to display your brand on our site, you can send us an email at admin@mooreexotics.com with ad images and other contractual details.",
  },
];

const mapStateToProps = ({ Auth }) => ({ user: Auth.user });
const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Membership);
